import { useEffect, useMemo, useState } from "react";
import { Box, Radio, RadioGroup, Stack, Typography } from "@mui/joy";

import { ControlledTextField, Dialog } from "../../../../../../../shared";
import {
    DEFAULT_EAGLEVIEW_PRODUCT_ID,
    EAGLEVIEW_ORDER_PRODUCTS,
} from "./products";
import type {
    EagleViewAddress,
    EagleViewPlaceOrderRequest,
    EagleViewPlaceOrderResult,
} from "./types";

type OrderReportModalProps = {
    open: boolean;
    onClose: () => void;
    /** Project property address — display only; not editable here. */
    initialAddress: EagleViewAddress;
    onPlaceOrder: (
        request: EagleViewPlaceOrderRequest,
    ) => Promise<EagleViewPlaceOrderResult | void>;
};

const sectionLabelSx = {
    textTransform: "uppercase" as const,
    fontWeight: "xl" as const,
    letterSpacing: "0.5px",
    color: "text.tertiary",
    mb: 0.75,
};

function normalizeAddress(address: EagleViewAddress): EagleViewAddress {
    return {
        street: address.street?.trim() || "",
        city: address.city?.trim() || "",
        state: address.state?.trim() || "",
        zip: address.zip?.trim() || "",
    };
}

function isAddressComplete(address: EagleViewAddress): boolean {
    const a = normalizeAddress(address);
    return Boolean(a.street && a.city && a.state && a.zip);
}

function formatAddressLine(address: EagleViewAddress): string {
    const a = normalizeAddress(address);
    const cityStateZip = [a.city, a.state, a.zip].filter(Boolean).join(", ");
    return [a.street, cityStateZip].filter(Boolean).join("\n");
}

export default function OrderReportModal({
    open,
    onClose,
    initialAddress,
    onPlaceOrder,
}: OrderReportModalProps) {
    const [productId, setProductId] = useState(DEFAULT_EAGLEVIEW_PRODUCT_ID);
    const [claimNumber, setClaimNumber] = useState("");
    const [poNumber, setPoNumber] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const address = useMemo(
        () => normalizeAddress(initialAddress),
        [initialAddress],
    );
    const addressComplete = isAddressComplete(address);
    const addressDisplay = formatAddressLine(address);

    useEffect(() => {
        if (!open) return;
        setProductId(DEFAULT_EAGLEVIEW_PRODUCT_ID);
        setClaimNumber("");
        setPoNumber("");
        setError(null);
        setSubmitting(false);
    }, [open]);

    const selectedProduct =
        EAGLEVIEW_ORDER_PRODUCTS.find((p) => p.productId === productId) ||
        EAGLEVIEW_ORDER_PRODUCTS[0];

    const canSubmit = addressComplete && !!selectedProduct && !submitting;

    const handleSubmit = async () => {
        if (!selectedProduct || !canSubmit) return;

        setSubmitting(true);
        setError(null);
        try {
            const request: EagleViewPlaceOrderRequest = {
                address,
                productId: selectedProduct.productId,
                deliveryProductId: selectedProduct.deliveryProductId,
                measurementRequestTypeId:
                    selectedProduct.measurementRequestTypeId,
                ...(claimNumber.trim()
                    ? { claimNumber: claimNumber.trim() }
                    : {}),
                ...(poNumber.trim() ? { poNumber: poNumber.trim() } : {}),
            };
            await onPlaceOrder(request);
            onClose();
        } catch (err: any) {
            setError(
                err?.message ||
                    "Failed to place EagleView order. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Order EagleView report"
            description="We’ll place a measurement order for this property. Measurements sync when the report is ready."
            maxWidth={520}
            actions={{
                cancel: {
                    label: "Cancel",
                    onClick: onClose,
                    disabled: submitting,
                },
                submit: {
                    label: submitting ? "Placing order…" : "Place order",
                    onClick: handleSubmit,
                    disabled: !canSubmit,
                    loading: submitting,
                    variant: "soft",
                    color: "primary",
                },
            }}
        >
            <Stack spacing={2.25}>
                {error && (
                    <Box
                        sx={{
                            px: 1.25,
                            py: 1,
                            borderRadius: "4px",
                            bgcolor: "danger.softBg",
                            border: "1px solid",
                            borderColor: "danger.outlinedBorder",
                        }}
                    >
                        <Typography level="body-sm" color="danger">
                            {error}
                        </Typography>
                    </Box>
                )}

                <Box>
                    <Typography level="body-xs" sx={sectionLabelSx}>
                        Property address
                    </Typography>
                    <Box
                        sx={{
                            px: 1.5,
                            py: 1.25,
                            borderRadius: "4px",
                            bgcolor: "background.level1",
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        {addressComplete ? (
                            <Typography
                                level="body-sm"
                                sx={{
                                    fontWeight: 500,
                                    whiteSpace: "pre-line",
                                    lineHeight: 1.55,
                                }}
                            >
                                {addressDisplay}
                            </Typography>
                        ) : (
                            <Typography level="body-sm" color="warning">
                                Property address is incomplete. Update it on the
                                Customer or project details before ordering.
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box>
                    <Typography level="body-xs" sx={sectionLabelSx}>
                        Report type
                    </Typography>
                    <RadioGroup
                        size="sm"
                        value={String(productId)}
                        onChange={(event) =>
                            setProductId(Number(event.target.value))
                        }
                        sx={{ gap: 0.75 }}
                    >
                        {EAGLEVIEW_ORDER_PRODUCTS.map((product) => {
                            const selected =
                                product.productId === productId;
                            return (
                                <Box
                                    key={product.productId}
                                    component="label"
                                    sx={{
                                        // Keep Radio overlay (if any) clipped to this card —
                                        // without relative, Joy's overlay can cover the whole modal.
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1.25,
                                        px: 1.5,
                                        py: 1.25,
                                        borderRadius: "4px",
                                        border: "1px solid",
                                        borderColor: selected
                                            ? "primary.solidBg"
                                            : "divider",
                                        bgcolor: selected
                                            ? "rgba(var(--standard-palette-primary-mainChannel) / 0.06)"
                                            : "background.surface",
                                        cursor: "pointer",
                                        transition:
                                            "border-color 0.15s ease, background-color 0.15s ease",
                                        "&:hover": {
                                            borderColor: selected
                                                ? "primary.solidBg"
                                                : "neutral.outlinedHoverBorder",
                                            bgcolor: selected
                                                ? "rgba(var(--standard-palette-primary-mainChannel) / 0.08)"
                                                : "background.level1",
                                        },
                                    }}
                                >
                                    <Radio
                                        value={String(product.productId)}
                                        sx={{ mt: 0.15 }}
                                    />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            level="body-sm"
                                            sx={{
                                                fontWeight: 600,
                                                color: selected
                                                    ? "primary.plainColor"
                                                    : "text.primary",
                                            }}
                                        >
                                            {product.label}
                                        </Typography>
                                        <Typography
                                            level="body-xs"
                                            sx={{
                                                color: "text.tertiary",
                                                mt: 0.15,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {product.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </RadioGroup>
                </Box>

                <Box>
                    <Typography level="body-xs" sx={sectionLabelSx}>
                        Optional references
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                            },
                            gap: 1.25,
                        }}
                    >
                        <ControlledTextField
                            label="Claim number"
                            value={claimNumber}
                            onChange={setClaimNumber}
                            placeholder="Optional"
                        />
                        <ControlledTextField
                            label="PO number"
                            value={poNumber}
                            onChange={setPoNumber}
                            placeholder="Optional"
                        />
                    </Box>
                </Box>

                <Typography
                    level="body-xs"
                    sx={{ color: "text.tertiary", lineHeight: 1.45 }}
                >
                    Ordering is asynchronous. After EagleView completes the
                    report, measurements will be pulled into this project
                    automatically.
                </Typography>
            </Stack>
        </Dialog>
    );
}
